import React, { FC, PureComponent, useEffect } from 'react';
import styled from 'styled-components';

import {
  Routes as RouterRoutes, Route, Navigate, useLocation,
} from 'react-router-dom';
import { connect } from 'react-redux';
import Fade from '@mui/material/Fade';

import { IAppState } from '../state/reducers';
import { Styling, Routes } from '../constants';
import Header from '../components/Header/component';
import SimpleSearch from '../components/Search/Simple/component';
import VirtualClipper from '../components/VirtualClipper/index';
import PublishProfiles from '../components/Publish/component';
import Exchange from '../components/Exchange/component';
import { getAllSettingsAction } from '../state/publishEndpoints';
import { getSecurityProfiles } from '../state/securityProfiles';
import { Product } from '../state/product';
import { getLookups } from '../state/lookups';
import { loadAdminTags as getAdminTags, loadOrgTags as getOrgTags, selectOrgTagsLoaded } from '../state/tags';
import {
  loadAllUsers,
  selectIsUserVaultSuperAdmin,
  selectIsUserExchangeAdmin,
  selectIsUserExchangeVendor,
} from '../state/user/userOrgRole';
import LiveEvents from '../components/LiveEvents/component';
import AdminDashboardContainer from '../components/AdminDashboardContainer/component';
import { getSystemSettings } from '../state/systemSettings';

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: ${Styling.MIN_WIDTH}px;
  height: calc(100% - var(--impersonation-banner-height, 0px));
  align-items: center;
  flex-direction: column;
`;

interface IStateProps {
  isSecurityProfilesLoaded: boolean;
  isLookupsLoaded: boolean;
  isTagsLoaded: boolean;
  isAdminTagsLoaded: boolean,
  loggedIn: boolean;
  isVaultSuperAdmin: boolean;
  isExchangeAdmin: boolean;
  isExchangeVendor: boolean;
  product: Product;
}

interface IDispatchProps {
  getAllPublishSettingsConnect: () => void;
  loadAllUsersConnect: (login: boolean) => void;
  getSecurityProfilesConnect: () => void;
  getLookupsConnect: () => void;
  getOrgTagsConnect: () => void;
  getAdminTagsConnect: () => void;
  getSystemSettingsConnect: () => void;
}

interface IReactChildNode {
  children?: React.ReactNode;
}

export type IProps = IStateProps & IDispatchProps & IReactChildNode;

const ApplicationLayout: FC<IProps> = ({
  loggedIn,
  isVaultSuperAdmin,
  isExchangeAdmin,
  isExchangeVendor,
  product,
  getAllPublishSettingsConnect,
  getSecurityProfilesConnect,
  getLookupsConnect,
  getSystemSettingsConnect,
  isSecurityProfilesLoaded,
  isLookupsLoaded,
  loadAllUsersConnect,
  isAdminTagsLoaded,
  getAdminTagsConnect,
}: IProps) => {
  useEffect(() => {
    const loadData = async () => {
      if (!loggedIn) {
        // load all users and set user state
        await loadAllUsersConnect(true);

        if (!isSecurityProfilesLoaded) {
          // initialize security profiles
          await getSecurityProfilesConnect();
        }

        if (!isLookupsLoaded) {
          await getLookupsConnect();
        }
        // To Be Implemented after MUI Upgrade that Fixes State Issues
        // const { isOrgTagsLoaded, getOrgTagsConnect } = this.props;
        // if (!isOrgTagsLoaded) {
        //   getOrgTagsConnect();
        // }
        if (!isAdminTagsLoaded) {
          await getAdminTagsConnect();
        }

        // Load in the publish endpoints
        await getAllPublishSettingsConnect();

        // Load in system settings
        await getSystemSettingsConnect();
      }
    };

    loadData();
  }, [getAllPublishSettingsConnect,
    getLookupsConnect,
    getSecurityProfilesConnect,
    getSystemSettingsConnect,
    isLookupsLoaded,
    isSecurityProfilesLoaded,
    loadAllUsersConnect,
    loggedIn]);
  const HomeComponent = SimpleSearch;

  return (
    <Fade in>
      <FlexContainer>
        {React.createElement(Header)}
        {loggedIn && product && (
          <RouterRoutes>
            <Route path={Routes.HOME} element={<HomeComponent />} />
            {((isVaultSuperAdmin || isExchangeAdmin || isExchangeVendor) && product === Product.Exchange)
              && <Route path={Routes.EXCHANGE} element={<Exchange />} />}
            <Route path={`${Routes.PUBLISH_PROFILES}/*`} element={<PublishProfiles />} />
            <Route path={Routes.MANAGE_LIVE_EVENTS} element={<LiveEvents />} />
            <Route path={Routes.VIRTUAL_CLIPPER} element={<VirtualClipper />} />
            <Route path={Routes.ADMIN_DASHBOARD} element={<AdminDashboardContainer />} />
            <Route path="*" element={<Navigate to={Routes.HOME} replace />} />
          </RouterRoutes>
        )}
      </FlexContainer>
    </Fade>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isSecurityProfilesLoaded: state.securityProfiles?.isLoaded ?? false,
  isLookupsLoaded: state.lookups?.isLoaded ?? false,
  isTagsLoaded: selectOrgTagsLoaded(state),
  isAdminTagsLoaded: state.tags?.isAdminTagsLoaded ?? false,
  loggedIn: state.user?.userOrgRole?.loggedIn ?? false,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  isExchangeAdmin: selectIsUserExchangeAdmin(state),
  isExchangeVendor: selectIsUserExchangeVendor(state),
  product: state.product?.target,
});

const mapDispatchToProps: IDispatchProps = {
  getAllPublishSettingsConnect: getAllSettingsAction,
  loadAllUsersConnect: loadAllUsers,
  getSecurityProfilesConnect: getSecurityProfiles,
  getLookupsConnect: getLookups,
  getOrgTagsConnect: getOrgTags,
  getAdminTagsConnect: getAdminTags,
  getSystemSettingsConnect: getSystemSettings,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ApplicationLayout);
